/**
 * Sticky sidebar on the three admin profile edit forms — venue, catering and
 * supplier. The stick itself is CSS: `_box.scss` gives the sidebar column
 * `position: sticky; top: 20px` at `$large`, so the sidebar travels within the
 * `.row` — its containing block — and comes to rest at the row's bottom edge.
 *
 * What CSS cannot express is the explicit stop marker. The venue form renders
 * `.js-sticky-sidebar-end` *inside* the content column, above the row's bottom
 * edge, to stop the sidebar early; the catering and supplier forms carry no
 * marker and fall through to the site `<footer>`, which sits *below* the row.
 * A sticky box is constrained so that its *margin* box stays inside the
 * containing block, so a bottom margin of `row.bottom - stop.top` brings the
 * column to rest on the stop element's top edge — for either sign, a positive
 * inset shortening the travel and a negative one extending it past the row.
 *
 * Both rects are viewport-relative, so that difference changes with layout and
 * not with scrolling. The margin is therefore applied for as long as the
 * breakpoint matches, and remeasured only when the row resizes.
 */

/**
 * Matches `$large` in `js/styles/rails-styles/modules/_box.scss`, which gates
 * both `position: sticky` and `.show-for-large-up` on the same column. Not
 * `satisfiesMediaQuery('large')`, which rounds to `64.063em` and so disagrees
 * with the stylesheet across a sliver of one pixel.
 */
const LARGE_UP_QUERY = 'only screen and (min-width: 64.0625em)';

/**
 * The sticky column. This has to sit on the same element as
 * `.m-box-sticky-column`, which is what `_box.scss` sticks: the inset below is
 * a margin on the sticky element, so a wrapper between the two would take the
 * margin while the stylesheet stuck something else, and the stop would quietly
 * stop working.
 */
const COLUMN_SELECTOR = '.js-sticky-sidebar';

/**
 * `querySelector` returns the first match in document order, so an explicit
 * `.js-sticky-sidebar-end` marker wins over the `footer` fallback wherever a
 * page has both.
 */
const STOP_SELECTOR = '.js-sticky-sidebar-end, footer';

type StickySidebar = {
  /** The grid column the stylesheet makes sticky. */
  column: HTMLElement;
  /** The Foundation `.row`, i.e. the sticky column's containing block. */
  row: HTMLElement;
  /** Where the sidebar should stop travelling. */
  stop: HTMLElement;
};

/**
 * The observer and the listener below are held in a closure with no teardown,
 * so a second call would leave a second set observing for the life of the page
 * with nothing able to reach them.
 */
let initialized = false;

export default function initializeStickySidebar(): void {
  if (initialized) return;
  initialized = true;

  const column = document.querySelector(COLUMN_SELECTOR);
  if (!(column instanceof HTMLElement)) return;

  const row = column.parentElement;
  if (!row) return;

  const stop = document.querySelector(STOP_SELECTOR);
  if (!(stop instanceof HTMLElement)) return;

  watchStopElement({ column, row, stop });
}

function watchStopElement({ column, row, stop }: StickySidebar): void {
  const largeUp = window.matchMedia(LARGE_UP_QUERY);
  let observer: ResizeObserver | null = null;

  // `removeProperty` rather than `style.marginBottom = ''`: jsdom's cssstyle
  // silently ignores an empty-string assignment through the camel-cased
  // property, so the assignment form would leave the tests unable to observe
  // the clear even though browsers honour it.
  function clearStop(): void {
    column.style.removeProperty('margin-bottom');
  }

  /**
   * Ends the column's sticky travel on the stop element's top edge. The margin
   * is cleared before measuring, because a bottom margin on an in-flow child
   * grows the row it sits in: measuring with the previous inset still applied
   * would feed it back into the next one.
   */
  function applyStop(): void {
    clearStop();
    const inset =
      row.getBoundingClientRect().bottom - stop.getBoundingClientRect().top;
    column.style.marginBottom = `${inset}px`;
  }

  function connect(): void {
    if (observer) return;

    observer = new ResizeObserver(applyStop);
    observer.observe(row);
    applyStop();
  }

  function disconnect(): void {
    observer?.disconnect();
    observer = null;
    clearStop();
  }

  /** Observes only at large-up; the sidebar is static on narrower screens. */
  function syncToBreakpoint(): void {
    if (largeUp.matches) {
      connect();
    } else {
      disconnect();
    }
  }

  largeUp.addEventListener('change', syncToBreakpoint);
  syncToBreakpoint();
}
